// ════════════════════════════════════════════════════════════════════
// Glass-i — Sales Tax Invoice pure UI helpers (Phase 1B).
// Invoice status vocabulary + editor-action gating + formatters. Kept SEPARATE
// from proformaHelpers so the proforma module is not touched. Reuses the
// mode-agnostic pure helpers (hasPermission, formatMoney) from proformaHelpers.
// ════════════════════════════════════════════════════════════════════

import { hasPermission, formatMoney } from '../proforma/proformaHelpers.js';

export { formatMoney };

// Area — exactly 3 dp with Indian grouping (CLAUDE.md rule 5A).
const NF_AREA = new Intl.NumberFormat('en-IN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
export function formatArea3(n) {
  const v = Number(n);
  return Number.isFinite(v) ? NF_AREA.format(v) : '0.000';
}

// ── Status vocabulary (draft / posted / cancelled) ──────────────────
// A NEW document is seeded with the shared 'PENDING' status (emptyHeader); it is
// treated as a draft here. Only 'posted' / 'cancelled' are terminal/read-only.
function isDraftLike(s) { return s !== 'posted' && s !== 'cancelled'; }
export function invoiceStatusLabel(status) {
  const s = String(status || 'draft').toLowerCase();
  if (s === 'posted') return 'Posted';
  if (s === 'cancelled') return 'Cancelled';
  return 'Draft';
}
export function invoiceStatusColor(status) {
  const s = String(status || 'draft').toLowerCase();
  if (s === 'posted') return 'green';
  if (s === 'cancelled') return 'red';
  return 'blue';
}
export function invoiceStatusUi(status) {
  const s = String(status || 'draft').toLowerCase();
  const editable = isDraftLike(s);
  return { readOnly: !editable, editable, label: invoiceStatusLabel(s), color: invoiceStatusColor(s) };
}

// ── Editor action visibility ─────────────────────────────────────────
// A DRAFT may be saved / edited / discarded / POSTED (Phase 2 GL posting). A
// POSTED invoice is read-only (no save/discard/post). There is deliberately NO
// posted-cancel / voucher-reversal action here (that is the next slice). Post is
// gated on the actual `erp.sales.invoice.post` PERMISSION — never a role name —
// so a future Accounts role inherits it without any code change.
export function invoiceEditorActions(header, permissions) {
  const isNew = !header || !header.id;
  const status = String((header && header.status) || 'draft').toLowerCase();
  const isDraft = status !== 'posted' && status !== 'cancelled';
  const isPosted = status === 'posted';
  const canCreate = hasPermission(permissions, 'erp.sales.invoice.create');
  const canEdit = hasPermission(permissions, 'erp.sales.invoice.edit');
  const canDiscard = hasPermission(permissions, 'erp.sales.invoice.discard');
  const canPost = hasPermission(permissions, 'erp.sales.invoice.post');
  const canCancel = hasPermission(permissions, 'erp.sales.invoice.cancel');
  return {
    saveNew: isNew && canCreate,
    update: !isNew && isDraft && canEdit,
    discard: !isNew && isDraft && canDiscard,
    // Post a persisted DRAFT to the ledger. Never shown for new/posted/cancelled.
    post: !isNew && isDraft && canPost,
    // Cancel a POSTED invoice (reverses the voucher). ONLY for a posted invoice
    // with the cancel permission — never for draft/cancelled. Distinct from discard.
    // Named postedCancel (not cancel*) so it never collides with the proforma
    // lifecycle "cancel" action guarded out of the shared editor.
    postedCancel: !isNew && isPosted && canCancel,
    print: false,   // print pipeline is a later phase
    clone: false,
    delete: false
  };
}

// Map a backend post() error code to a clear, user-facing message. We key on the
// CODE (never raw SQL / provider text). Falls back to the given message.
export function postErrorMessage(code, fallback) {
  return ({
    VOUCHER_PERIOD_MISSING: 'This Tax Invoice cannot be posted because no accounting period is configured for the invoice date. Ask Accounts/Admin to set up the period.',
    VOUCHER_PERIOD_CLOSED: 'This Tax Invoice cannot be posted because the accounting period for the invoice date is closed.',
    VOUCHER_PERIOD_LOCKED: 'This Tax Invoice cannot be posted because the accounting period for the invoice date is locked.',
    SALES_LEDGER_MAPPING_INCOMPLETE: 'Required accounting ledger configuration is missing. Please contact Accounts/Admin.',
    ACCOUNTING_LEDGER_MISSING: 'Required accounting ledger configuration is missing. Please contact Accounts/Admin.',
    INVOICE_NOT_POSTABLE: 'This Tax Invoice has no value to post to the ledger.',
    INVOICE_NOT_DRAFT: 'This Tax Invoice is already posted (or is no longer a draft).',
    INVOICE_NOT_FOUND: 'This Tax Invoice could not be found.',
    FORBIDDEN: 'You do not have permission to post Tax Invoices.',
    VALIDATION_ERROR: 'Please correct the highlighted fields and try again.'
  })[String(code || '')] || fallback || 'Could not post the Tax Invoice.';
}

// Map a backend cancelPostedInvoice() error CODE to clear copy. Cancellation is a
// posted-only, reason-required accounting reversal — its failure modes differ from
// posting, so it has its own map (still code-keyed, never raw SQL).
export function cancelErrorMessage(code, fallback) {
  return ({
    CANCELLATION_REASON_REQUIRED: 'A cancellation reason is required to cancel a posted Tax Invoice.',
    CANCELLATION_REASON_TOO_LONG: 'The cancellation reason is too long.',
    INVOICE_NOT_POSTED: 'Only a POSTED Tax Invoice can be cancelled. A draft must be discarded instead.',
    INVOICE_ALREADY_CANCELLED: 'This Tax Invoice is already cancelled.',
    INVOICE_NOT_FOUND: 'This Tax Invoice could not be found.',
    POSTED_VOUCHER_MISSING: 'This posted invoice has no linked accounting voucher, so it cannot be reversed. Please contact Accounts/Admin.',
    REVERSAL_ALREADY_EXISTS: 'This invoice has already been reversed.',
    VOUCHER_CANCEL_PERIOD_LOCKED: 'This Tax Invoice cannot be cancelled because the accounting period for its date is closed or locked. Reopen the period first.',
    VOUCHER_PERIOD_CLOSED: 'This Tax Invoice cannot be cancelled because the accounting period for its date is closed.',
    VOUCHER_PERIOD_LOCKED: 'This Tax Invoice cannot be cancelled because the accounting period for its date is locked.',
    IRN_CANCELLATION_REQUIRED: 'This invoice has a generated IRN (e-Invoice). Cancel the IRN on the GST portal first — local cancellation is blocked.',
    EWAY_CANCELLATION_REQUIRED: 'This invoice has a generated e-Way Bill. Cancel the e-Way Bill first — local cancellation is blocked.',
    FORBIDDEN: 'You do not have permission to cancel posted Tax Invoices.'
  })[String(code || '')] || fallback || 'Could not cancel the Tax Invoice.';
}

// ── GST-type / release-status / source labels ────────────────────────
export function gstTypeLabel(t) {
  const s = String(t || '').toLowerCase();
  return ({
    b2b: 'B2B', b2cl: 'B2CL', b2cs: 'B2CS', export: 'Export', sez: 'SEZ',
    deemed_export: 'Deemed Export', credit_note: 'Credit Note', debit_note: 'Debit Note'
  })[s] || (s ? s.toUpperCase() : '—');
}
export function releaseStatusTag(rs) {
  const s = String(rs || '').toUpperCase();
  return ({
    READY: { color: 'green', label: 'Ready' },
    BLOCKED_IRN: { color: 'orange', label: 'Blocked (IRN)' },
    RELEASED: { color: 'blue', label: 'Released' },
    CANCELLED: { color: 'red', label: 'Cancelled' }
  })[s] || { color: 'default', label: s || '—' };
}
export function eInvoiceStatusLabel(s) {
  const v = String(s || 'not_applicable').toLowerCase();
  return ({ not_applicable: '—', pending: 'Pending', generated: 'Generated', failed: 'Failed', cancelled: 'Cancelled' })[v] || v;
}
export function sourceLabel(src) {
  const s = String(src || '').toLowerCase();
  return ({ manual: 'Manual', proforma_import: 'From Proforma', change_order: 'Change Order' })[s] || (s || '—');
}

// ── List tabs / params ───────────────────────────────────────────────
export const INVOICE_STATUS_TABS = Object.freeze([
  { key: 'all', label: 'All', status: null },
  { key: 'draft', label: 'Draft', status: 'draft' },
  { key: 'posted', label: 'Posted', status: 'posted' },
  { key: 'cancelled', label: 'Cancelled', status: 'cancelled' }
]);

export function buildInvoiceListParams({ status, search, customerId, gstType, source, proformaId, dateFrom, dateTo, limit, offset } = {}) {
  const p = {};
  if (status && status !== 'all') p.status = status;
  if (search) p.search = search;
  if (customerId) p.customerId = customerId;
  if (gstType) p.gstType = gstType;          // reserved for backend widen; harmless if ignored
  if (source) p.source = source;
  if (proformaId) p.proformaId = proformaId;
  if (dateFrom) p.dateFrom = dateFrom;
  if (dateTo) p.dateTo = dateTo;
  if (limit != null) p.limit = limit;
  if (offset != null) p.offset = offset;
  return p;
}

// ── Derived proforma → invoice badge. Derived from Sales Invoice data; never
// mutates proforma state. Canonical states (backend-derived): NOT_INVOICED |
// DRAFT_INVOICE | POSTED. A discarded/cancelled invoice reads as NOT_INVOICED.
export function proformaInvoiceBadge(state) {
  const s = String(state || 'NOT_INVOICED').toUpperCase();
  return ({
    NOT_INVOICED: { color: 'default', label: 'Not Invoiced' },
    DRAFT_INVOICE: { color: 'gold', label: 'Draft Invoice' },
    POSTED: { color: 'green', label: 'Posted' }
  })[s] || { color: 'default', label: 'Not Invoiced' };
}

// Event-type → human label for the activity timeline.
export function eventTypeLabel(t) {
  const s = String(t || '').toUpperCase();
  return ({
    CREATED: 'Invoice created',
    IMPORTED_FROM_PROFORMA: 'Imported from Proforma',
    UPDATED: 'Invoice updated',
    DRAFT_DISCARDED: 'Draft discarded',
    POSTED: 'Invoice posted',
    VOUCHER_POSTED: 'Accounting voucher posted',
    POST_FAILED: 'Posting failed',
    VOUCHER_REVERSED: 'Accounting voucher reversed',
    POSTED_INVOICE_CANCELLED: 'Posted invoice cancelled',
    CANCELLED: 'Invoice cancelled'
  })[s] || s;
}

// ── mm → inches, as a shop floor reads them ─────────────────────────
//
// Glass sizes are called out in sixteenths, not decimals: "46 3/4", not
// "46.75". The proforma editor and the work-order copies already work in 1/16
// steps, so the import picker matches them rather than introducing a third way
// of writing the same measurement.
//
// DISPLAY ONLY. mm stays the stored truth everywhere; nothing is ever billed or
// recomputed from this string.
export const MM_PER_INCH = 25.4;

export function mmToFractionalInches(mm, denom = 16) {
  const v = Number(mm);
  if (!Number.isFinite(v) || v <= 0) return '';
  const totalSixteenths = Math.round((v / MM_PER_INCH) * denom);
  let whole = Math.floor(totalSixteenths / denom);
  let num = totalSixteenths % denom;
  if (num === 0) return `${whole}`;
  // Reduce the fraction so 8/16 reads 1/2 — a fitter reading "46 8/16" has to
  // stop and simplify it, which is exactly the friction this avoids.
  let den = denom;
  const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
  const g = gcd(num, den);
  num /= g; den /= g;
  return whole === 0 ? `${num}/${den}` : `${whole} ${num}/${den}`;
}
